import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { Modal, Tooltip } from 'antd'
import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { IcoRemove } from '@/utils/icons'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import classes from './index.module.css'
import headerClasses from '@common/Header/index.module.css'
import cn from 'classnames'
import Input from '@pages/Admin/CRM/components/base/Input/Input'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useAccountActions } from '@/hooks/useAccountActions'
import { AuthService, idUserAuthorizedOnTheAuthorizationPage } from '@/services/AuthService'
import { integrationsApi, integrationRecipientApi } from '@/api/integrations'
import { isError } from '@/helpers/checkQuery'
import {
  dispatchNotificationIntegrationRefetch,
  getNotificationIntegration,
  getNotificationIntegrationRefetchEvent,
  isActivationRequiredForRole,
  NOTIFICATION_INTEGRATION_UI,
  type NotificationIntegrationProvider,
} from '@/helpers/notificationIntegration'

export interface ActivateNotificationIntegrationProps {
  provider: NotificationIntegrationProvider
  open?: boolean
  setOpen?: (open: boolean) => void
  necessarily?: boolean
  integrationId?: number | null
  /** Сбой подготовки ссылки / API — можно закрыть даже при necessarily. */
  onActivationUnavailable?: () => void
}

const ActivateNotificationIntegration: FC<ActivateNotificationIntegrationProps> = ({
  provider,
  open = true,
  setOpen = () => {},
  necessarily = false,
  integrationId = null,
  onActivationUnavailable,
}) => {
  const addAlert = useAddAlert()
  const ui = NOTIFICATION_INTEGRATION_UI[provider]
  const profile = useSelector(selectProfile)
  const userId = profile?.id
  const { loadingAccounts, disconnectAccount, onLogin } = useAccountActions()

  const [isActive, setIsActive] = useState(open)
  const [submitting, setSubmitting] = useState(false)
  const [code, setCode] = useState('')
  const [loadingLink, setLoadingLink] = useState(false)
  const [activationUrl, setActivationUrl] = useState('')
  const [linkFailed, setLinkFailed] = useState(false)
  const [submitErrorText, setSubmitErrorText] = useState<string | null>(null)
  const [copiedCommand, setCopiedCommand] = useState(false)
  const onActivationUnavailableRef = useRef(onActivationUnavailable)
  onActivationUnavailableRef.current = onActivationUnavailable

  const canDismiss = !necessarily || linkFailed

  useEffect(() => {
    setIsActive(open)
  }, [open])

  useEffect(() => {
    setOpen(isActive)
  }, [isActive, setOpen])

  useEffect(() => {
    if (!isActive) return

    setCode('')
    setSubmitErrorText(null)
    setActivationUrl('')
    setCopiedCommand(false)
    setLinkFailed(false)

    if (!userId || !integrationId) return

    let cancelled = false

    setLoadingLink(true)

    const markUnavailable = () => {
      setActivationUrl('')
      setLinkFailed(true)
      onActivationUnavailableRef.current?.()
    }

    integrationRecipientApi
      .startActivation(integrationId, {
        user_id: Number(userId),
        origin: window.location.origin,
      })
      .then((res) => {
        if (cancelled) return
        if (isError(res as any)) {
          markUnavailable()
          return
        }
        const url = String(res?.data?.data?.activation_url ?? '')
        if (!url) {
          markUnavailable()
          return
        }
        setActivationUrl(url)
        setLinkFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        markUnavailable()
      })
      .finally(() => {
        if (cancelled) return
        setLoadingLink(false)
      })

    return () => {
      cancelled = true
    }
  }, [integrationId, isActive, userId])

  const handlerSubmit = async () => {
    if (!userId || !integrationId || !code) return

    setSubmitting(true)
    setSubmitErrorText(null)
    try {
      const res = await integrationRecipientApi.activate(integrationId, {
        user_id: Number(userId),
        code,
      })

      if (isError(res as any)) {
        setSubmitErrorText('Неверный код.')
        return
      }

      addAlert({
        title: 'Успешно',
        text: ui.successAttachText,
        type: 'success',
      })

      setIsActive(false)
      dispatchNotificationIntegrationRefetch(provider)
    } catch {
      setSubmitErrorText('Неверный код.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogout = async () => {
    if (loadingAccounts?.length) return

    const authorizedParentId = localStorage.getItem(
      idUserAuthorizedOnTheAuthorizationPage,
    )

    if (authorizedParentId && authorizedParentId !== String(profile?.id)) {
      await disconnectAccount(profile?.id)
      onLogin(authorizedParentId)
    } else {
      AuthService.logout()
    }
  }

  const handleDismiss = () => {
    if (!canDismiss) return
    setIsActive(false)
    onActivationUnavailable?.()
  }

  const titleText = necessarily
    ? `Необходимо активировать ${ui.shortTitle}`
    : `Привяжите ${ui.shortTitle}`

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command)
      setCopiedCommand(true)
      window.setTimeout(() => setCopiedCommand(false), 1500)
    } catch {
      addAlert({
        title: 'Не удалось скопировать',
        type: 'error',
      })
    }
  }

  const instructionContent = (() => {
    if (!activationUrl) {
      return loadingLink
        ? 'Готовим персональную ссылку для привязки...'
        : 'Не удалось подготовить ссылку для привязки. Закройте окно и проверьте настройки интеграции уведомлений.'
    }

    const command =
      'activationCommand' in ui ? ui.activationCommand : undefined
    if (!command) return ui.instructionWithLink

    const [before, ...rest] = ui.instructionWithLink.split(command)
    return (
      <>
        {before}
        <span className={classes.commandChip}>
          <span className={classes.command}>{command}</span>
          <Tooltip title={copiedCommand ? 'Скопировано' : 'Скопировать'}>
            <button
              type="button"
              className={classes.copyButton}
              onClick={() => handleCopyCommand(command)}
              aria-label={`Скопировать «${command}»`}
            >
              {copiedCommand ? (
                <CheckOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              ) : (
                <CopyOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              )}
            </button>
          </Tooltip>
        </span>
        {rest.join(command)}
      </>
    )
  })()

  const logoutButton =
    necessarily && isActive && !linkFailed && typeof document !== 'undefined'
      ? createPortal(
          <button
            type="button"
            className={cn(
              headerClasses.button,
              classes.logoutButton,
              'trackClickWhilePerformingWork',
            )}
            title="Выйти"
            onClick={handleLogout}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M7 7.13193V6.61204C7 4.46614 7 3.3932 7.6896 2.79511C8.37919 2.19703 9.44136 2.34877 11.5657 2.65224L15.8485 3.26408C18.3047 3.61495 19.5327 3.79039 20.2664 4.63628C21 5.48217 21 6.72271 21 9.20377V14.7962C21 17.2773 21 18.5178 20.2664 19.3637C19.5327 20.2096 18.3047 20.385 15.8485 20.7359L11.5657 21.3478C9.44136 21.6512 8.37919 21.803 7.6896 21.2049C7 20.6068 7 19.5339 7 17.388V17.066"
                stroke="var(--color-text-main)"
                strokeWidth="1.5"
              />
              <path
                d="M16 12L16.5857 11.5315L16.9605 12L16.5857 12.4685L16 12ZM4 12.75C3.58579 12.75 3.25 12.4142 3.25 12C3.25 11.5858 3.58579 11.25 4 11.25V12.75ZM12.5857 6.53148L16.5857 11.5315L15.4143 12.4685L11.4143 7.46852L12.5857 6.53148ZM16.5857 12.4685L12.5857 17.4685L11.4143 16.5315L15.4143 11.5315L16.5857 12.4685ZM16 12.75H4V11.25H16V12.75Z"
                fill="var(--color-text-main)"
              />
            </svg>
          </button>,
          document.body,
        )
      : null

  return (
    <>
      {logoutButton}
      <Modal
        open={isActive}
        closable={canDismiss}
        maskClosable={canDismiss}
        keyboard={canDismiss}
        onCancel={handleDismiss}
        footer={null}
        title={
          <div className={mclasses.modalHeader}>
            <p className={mclasses.modalHeaderTitle}>{titleText}</p>
            {canDismiss && (
              <div className={mclasses.closeIcon} onClick={handleDismiss}>
                <IcoRemove />
              </div>
            )}
          </div>
        }
        centered
        className={cn(amclasses.modal)}
      >
        <div className={classes.description}>
          <p>{instructionContent}</p>

          {activationUrl && (
            <p>
              Перейдите по ссылке:{' '}
              <a
                href={activationUrl}
                className={classes.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ui.openLinkLabel}
              </a>
            </p>
          )}
        </div>

        <Input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          name={`${provider}-notification-activation-code`}
          placeholder="Код подтверждения"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={linkFailed || loadingLink}
        />

        {submitErrorText && <div className={classes.errorText}>{submitErrorText}</div>}

        <Button
          isLoading={submitting}
          onClick={handlerSubmit}
          className={classes.submitButton}
          disabled={!code || submitting || !integrationId || linkFailed || loadingLink}
        >
          Активировать
        </Button>
      </Modal>
    </>
  )
}

export const RequiredActivateNotificationIntegration: FC<{
  provider: NotificationIntegrationProvider
}> = ({ provider }) => {
  const profile = useSelector(selectProfile)
  const [needActivate, setNeedActivate] = useState(false)
  const [integrationId, setIntegrationId] = useState<number | null>(null)
  const [fetchKey, setFetchKey] = useState(0)

  useEffect(() => {
    const eventName = getNotificationIntegrationRefetchEvent(provider)
    const handler = () => setFetchKey((k) => k + 1)
    window.addEventListener(eventName, handler)
    return () => window.removeEventListener(eventName, handler)
  }, [provider])

  useEffect(() => {
    let cancelled = false

    if (!profile?.id || !profile?.role) {
      setNeedActivate(false)
      setIntegrationId(null)
      return
    }

    integrationsApi
      .getAll(provider)
      .then((res) => {
        if (cancelled) return null
        if (isError(res as any)) {
          setNeedActivate(false)
          setIntegrationId(null)
          return null
        }

        const items = res?.data?.data ?? res?.data ?? []
        const integration = getNotificationIntegration(items)

        if (!integration || integration.status !== 'connected') {
          setNeedActivate(false)
          setIntegrationId(null)
          return null
        }

        if (!isActivationRequiredForRole(profile.role, integration)) {
          setNeedActivate(false)
          setIntegrationId(null)
          return null
        }

        setIntegrationId(integration.id)

        return integrationRecipientApi
          .checkIsActive(integration.id, Number(profile.id))
          .then((bindingRes: any) => {
            if (cancelled) return
            // axios error-interceptor часто резолвит error без reject —
            // без isError ложное is_active=false → lock-модалка.
            if (isError(bindingRes)) {
              setNeedActivate(false)
              setIntegrationId(null)
              return
            }
            const isActive =
              bindingRes?.data?.data?.is_active ??
              bindingRes?.data?.is_active
            setNeedActivate(isActive === false)
          })
      })
      .catch(() => {
        if (cancelled) return
        setNeedActivate(false)
        setIntegrationId(null)
      })

    return () => {
      cancelled = true
    }
  }, [profile?.id, profile?.role, provider, fetchKey])

  if (!needActivate || !integrationId) return null

  return (
    <ActivateNotificationIntegration
      provider={provider}
      necessarily={true}
      integrationId={integrationId}
      onActivationUnavailable={() => {
        setNeedActivate(false)
        setIntegrationId(null)
      }}
    />
  )
}

export default ActivateNotificationIntegration
